/**
 * Genera el PDF de contrato del cliente (Letter US).
 * Si el contrato no tiene folio, lo reserva atómicamente desde el backend.
 *
 * Acepta un callback opcional `onFolioAssigned(contractId, folio)` para que la app
 * propague el nuevo folio al estado global de Contratos.
 */
import axios from "axios";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { PICPARTY_LOGO_BASE64 } from "../../../lib/picpartyLogo";
import { parseLocalDate } from "../../../lib/dateUtils";
import { AVISO_URL } from "../../../lib/avisoPrivacidad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Dibuja un checkbox cuadrado (vacío o marcado con una X) en (x, yTop). */
const drawCheckbox = (pdf, x, yTop, checked, size = 3.5) => {
  pdf.setDrawColor(50, 50, 50);
  pdf.setFillColor(255, 255, 255);
  pdf.rect(x, yTop, size, size, "S");
  if (checked) {
    pdf.setFont(undefined, "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(40, 40, 40);
    pdf.text("X", x + size / 2, yTop + size - 0.6, { align: "center" });
  }
};

export const printContractPDF = async (contract, { onFolioAssigned } = {}) => {
    // QR del aviso de privacidad (header de ambas hojas)
    let qrDataUrl = null;
    try {
      const QRCode = (await import("qrcode")).default;
      qrDataUrl = await QRCode.toDataURL(AVISO_URL, { width: 300, margin: 1, errorCorrectionLevel: "M", color: { dark: "#000000", light: "#FFFFFF" } });
    } catch (e) {
      console.error("Error generando QR del aviso de privacidad:", e);
    }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const formatMXN = (amt) => `$${Number(amt).toLocaleString('es-MX')} MXN`;

    /** Dibuja el bloque de QR + etiquetas del aviso de privacidad, entre el logo y el folio. */
    const drawPrivacyQR = (x, y, size) => {
      if (!qrDataUrl) return;
      try {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(x - 1, y - 1, size + 2, size + 11, 'F');
        pdf.addImage(qrDataUrl, 'PNG', x, y, size, size);
        pdf.setFontSize(5.5);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(60, 60, 60);
        pdf.text("AVISO DE PRIVACIDAD", x + size / 2, y + size + 3, { align: 'center' });
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(5);
        pdf.setTextColor(110, 110, 110);
        pdf.text("Escanea para consultarlo", x + size / 2, y + size + 6.3, { align: 'center' });
      } catch (e) {
        console.error("Error insertando QR de privacidad en PDF:", e);
      }
    };

    // ========== FOLIO: usar el ya asignado o pedirlo atómicamente al backend ==========
    let folio = contract.folio;
    if (!folio) {
      try {
        // Reserva atómica desde el contador en MongoDB
        const res = await axios.get(`${API}/folios/next/contract_folio`);
        folio = res.data.folio;
        // Persistir el folio en el contrato
        await axios.put(`${API}/contracts/${contract.id}/folio`, null, { params: { folio } });
        contract.folio = folio;
        if (onFolioAssigned) onFolioAssigned(contract.id, folio);
      } catch (e) {
        console.error("No se pudo obtener folio del backend, usando fallback local:", e);
        const lastFolio = parseInt(localStorage.getItem('lastContractFolio') || '343', 10);
        const newFolioNum = lastFolio + 1;
        localStorage.setItem('lastContractFolio', newFolioNum.toString());
        folio = `PIC-${newFolioNum}`;
      }
    }

    // ========== FUNCIÓN PARA FOOTER EN AMBAS PÁGINAS ==========
    const addFooter = (pageNum, totalPages) => {
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text("(614) 272 5008 | SINCE 2014 | WWW.PICPARTY.NET", pageWidth / 2, pageHeight - 12, { align: 'center' });
      pdf.text(`Pagina ${pageNum} de ${totalPages}`, pageWidth / 2, pageHeight - 7, { align: 'center' });
    };

    // ==================== PÁGINA 1: CLÁUSULAS LEGALES ====================

    // === HEADER CON LOGO, QR DE PRIVACIDAD Y FOLIO ===
    // Logo a la izquierda (imagen embebida para evitar CORS)
    try {
      pdf.addImage(PICPARTY_LOGO_BASE64, 'PNG', margin, 5, 38, 38);
    } catch(e) {
      console.error("Error cargando logo:", e);
      pdf.setFontSize(18);
      pdf.setTextColor(26, 11, 46);
      pdf.setFont(undefined, 'bold');
      pdf.text("PIC PARTY", margin, 25);
    }

    // Servicios debajo del logo
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(80, 80, 80);
    pdf.text("Cabina de Fotos | Video 360 | Key Moments", margin + 42, 28);

    // QR del aviso de privacidad, entre el logo y el folio (mínimo 2cm)
    drawPrivacyQR(148, 3, 20);

    // Folio y etiqueta CONTRATO a la derecha
    pdf.setFontSize(11);
    pdf.setTextColor(26, 11, 46);
    pdf.setFont(undefined, 'bold');
    pdf.text(folio, pageWidth - margin, 15, { align: 'right' });
    pdf.setFillColor(26, 11, 46);
    pdf.roundedRect(pageWidth - margin - 28, 18, 28, 8, 1, 1, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.text("CONTRATO", pageWidth - margin - 14, 23.5, { align: 'center' });

    // === TÍTULO PRINCIPAL ===
    let y = 48;
    pdf.setTextColor(26, 11, 46);
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text("CONTRATO DE PRESTACION DE SERVICIOS", pageWidth / 2, y, { align: 'center' });
    y += 8;

    // Fecha y lugar
    pdf.setFontSize(9);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(60, 60, 60);
    const fechaContrato = new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    pdf.text(`Fecha: ${fechaContrato}`, margin, y);
    pdf.text("Chihuahua, Chih.", pageWidth - margin, y, { align: 'right' });
    y += 10;

    // === DATOS DEL CLIENTE / EVENTO ===
    // Fecha del evento: SIEMPRE parsear como fecha local (AAAA-MM-DD sin hora) para
    // que el día de la semana no se recorra por interpretación UTC.
    let fechaEvento = contract.event_date || '';
    try {
      const dateObj = parseLocalDate(contract.event_date);
      if (dateObj) {
        fechaEvento = dateObj.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      }
    } catch(e) {}

    const hasComment = !!(contract.service_time_comment && contract.service_time_comment.trim());
    const baseRowsBottom = contract.salon ? 24 : 18;
    const commentLineHeight = 4.2;
    const commentTextWidth = pageWidth - (margin * 2) - 6;
    let commentLines = [];
    if (hasComment) {
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'bold'); // medir con la misma fuente (negrita) con la que se imprime
      commentLines = pdf.splitTextToSize(`Comentario: ${contract.service_time_comment.trim()}`, commentTextWidth);
    }
    const boxHeight = hasComment
      ? baseRowsBottom + 3 + (commentLines.length * commentLineHeight) + 3
      : baseRowsBottom + 2;

    pdf.setFillColor(245, 245, 245);
    pdf.rect(margin, y, pageWidth - (margin * 2), boxHeight, 'F');
    pdf.setDrawColor(200, 200, 200);
    pdf.rect(margin, y, pageWidth - (margin * 2), boxHeight, 'S');

    pdf.setFontSize(9);
    pdf.setTextColor(40, 40, 40);
    pdf.setFont(undefined, 'bold');
    pdf.text("Cliente:", margin + 3, y + 6);
    pdf.text("Telefono:", margin + 3, y + 12);
    pdf.text("Fecha del Evento:", margin + 3, y + 18);

    pdf.setFont(undefined, 'normal');
    pdf.text(contract.client_name || '', margin + 25, y + 6);
    pdf.text(contract.client_phone || '', margin + 25, y + 12);
    pdf.text(fechaEvento, margin + 40, y + 18);

    // Horario Evento (columna derecha, fila 2)
    if (contract.event_time) {
      pdf.setFont(undefined, 'bold');
      pdf.text("Horario Evento:", pageWidth / 2 + 8, y + 12);
      pdf.setFont(undefined, 'normal');
      pdf.text(contract.event_time, pageWidth / 2 + 42, y + 12);
    }

    // Hora de Inicio del servicio (columna derecha, fila 3)
    if (contract.service_time) {
      pdf.setFont(undefined, 'bold');
      pdf.text("Hora de Inicio:", pageWidth / 2 + 8, y + 18);
      pdf.setFont(undefined, 'normal');
      pdf.text(contract.service_time, pageWidth / 2 + 42, y + 18);
    }

    // Salón / Lugar del Evento (si existe)
    if (contract.salon) {
      pdf.setFont(undefined, 'bold');
      pdf.text("Salon:", margin + 3, y + 24);
      pdf.setFont(undefined, 'normal');
      pdf.text(contract.salon, margin + 25, y + 24);
    }

    // Comentarios del horario del servicio (debajo de Hora de Inicio, en negrita, dentro del recuadro)
    if (hasComment) {
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      pdf.text(commentLines, margin + 3, y + baseRowsBottom + 3 + commentLineHeight);
      pdf.setFont(undefined, 'normal');
    }

    y += boxHeight + 8;

    // === 6 CLÁUSULAS LEGALES ===
    const lineHeight = 4.5;
    const clauseGap = 3.5;
    const textWidth = pageWidth - (margin * 2);
    pdf.setFontSize(10);

    // Cláusula 1 - OBJETO
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(26, 11, 46);
    pdf.text("1. OBJETO", margin, y);
    y += lineHeight;
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(50, 50, 50);
    const cl1 = 'PIC PARTY, representada por Adan Octavio Irigoyen Arias ("EL PRESTADOR"), se compromete a proporcionar los servicios de entretenimiento fotografico detallados en la pagina 2 de este contrato.';
    const lines1 = pdf.splitTextToSize(cl1, textWidth);
    pdf.text(lines1, margin, y);
    y += lines1.length * lineHeight + clauseGap;

    // Cláusula 2 - PAGO
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(26, 11, 46);
    pdf.text("2. PAGO", margin, y);
    y += lineHeight;
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(50, 50, 50);
    const anticipoMonto = contract.anticipo_amount || 0;
    const saldoPendiente = (contract.net_price || 0) - anticipoMonto;
    const cl2 = `Se requiere un anticipo de ${formatMXN(anticipoMonto)} para confirmar la fecha. El saldo restante (${formatMXN(saldoPendiente)}) debe liquidarse antes o el dia del evento.`;
    const lines2 = pdf.splitTextToSize(cl2, textWidth);
    pdf.text(lines2, margin, y);
    y += lines2.length * lineHeight + clauseGap;

    // Cláusula 3 - RESPONSABILIDAD
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(26, 11, 46);
    pdf.text("3. RESPONSABILIDAD", margin, y);
    y += lineHeight;
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(50, 50, 50);
    const cl3 = 'El cliente es responsable de cualquier perjuicio causado al equipo durante el evento por invitados o condiciones del lugar. En caso de perjuicio, el cliente cubrira el costo de reparacion o reposicion.';
    const lines3 = pdf.splitTextToSize(cl3, textWidth);
    pdf.text(lines3, margin, y);
    y += lines3.length * lineHeight + clauseGap;

    // Cláusula 4 - REQUERIMIENTOS
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(26, 11, 46);
    pdf.text("4. REQUERIMIENTOS", margin, y);
    y += lineHeight;
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(50, 50, 50);
    const cl4 = 'El cliente debe garantizar un espacio minimo de 3x3 metros con conexion electrica 110V estable a menos de 10 metros del area de servicio. Por condiciones climaticas adversas fuera de nuestro control, si el servicio es en exterior, PicParty podra cancelar sin devolucion del anticipo. Si el lugar del evento no proporciona las facilidades minimas requeridas para operar, PicParty no se hace responsable por retrasos o cancelacion del servicio.';
    const lines4 = pdf.splitTextToSize(cl4, textWidth);
    pdf.text(lines4, margin, y);
    y += lines4.length * lineHeight + clauseGap;

    // Cláusula 5 - CANCELACIÓN
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(26, 11, 46);
    pdf.text("5. CANCELACION", margin, y);
    y += lineHeight;
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(50, 50, 50);
    const cl5 = 'En caso de cancelacion, el anticipo no es reembolsable. Puede aplicarse a otra fecha sujeto a disponibilidad, con cargo adicional del 15% por reprogramacion.';
    const lines5 = pdf.splitTextToSize(cl5, textWidth);
    pdf.text(lines5, margin, y);
    y += lines5.length * lineHeight + clauseGap;

    // Cláusula 6 - AUTORIZACIÓN DE PUBLICACIÓN
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(26, 11, 46);
    pdf.text("6. AUTORIZACION DE PUBLICACION", margin, y);
    y += lineHeight;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(8.5);
    const clauseLineHeight = 3.8;
    pdf.setTextColor(50, 50, 50);
    const cl6 = 'Las fotografias del evento se suben a la galeria del evento en picparty.net, con acceso por link o codigo QR. Durante el evento, los invitados pueden ver y descargar sus fotos con el QR de la cabina; esto forma parte del servicio. EL CLIENTE elige:';
    const lines6 = pdf.splitTextToSize(cl6, textWidth);
    pdf.text(lines6, margin, y);
    y += lines6.length * clauseLineHeight + 2.5;

    const pubAuth = contract.publication_authorization || 'pendiente';
    const checkboxSize = 3.5;
    const checkboxTextWidth = textWidth - checkboxSize - 3;

    // Opción SÍ
    drawCheckbox(pdf, margin, y - 2.6, pubAuth === 'si', checkboxSize);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(50, 50, 50);
    const cl6b = 'SI autorizo publicar la galeria en picparty.net y usar algunas fotos como muestra en la pagina web y redes sociales.';
    const lines6b = pdf.splitTextToSize(cl6b, checkboxTextWidth);
    pdf.text(lines6b, margin + checkboxSize + 3, y);
    y += lines6b.length * clauseLineHeight + 2;

    // Opción NO
    drawCheckbox(pdf, margin, y - 2.6, pubAuth === 'no', checkboxSize);
    const cl6c = 'NO autorizo: la galeria no se publica en la pagina y las fotos no se usan con fines promocionales.';
    const lines6c = pdf.splitTextToSize(cl6c, checkboxTextWidth);
    pdf.text(lines6c, margin + checkboxSize + 3, y);
    y += lines6c.length * clauseLineHeight + 2.5;

    // Nota de escuelas/instituciones
    pdf.setFont(undefined, 'italic');
    pdf.setFontSize(8);
    pdf.setTextColor(70, 70, 70);
    const cl6d = 'En eventos de escuelas o instituciones educativas, PicParty no publica la galeria ni usa las fotos con fines promocionales, salvo autorizacion expresa y por escrito de la institucion o de los padres o tutores.';
    const lines6d = pdf.splitTextToSize(cl6d, textWidth);
    pdf.text(lines6d, margin, y);
    y += lines6d.length * 3.6 + clauseGap;

    // Link del aviso de privacidad (clicable), referenciando el QR del encabezado
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(90, 90, 90);
    const privacyNote = 'Aviso de privacidad: escanea el codigo QR del encabezado o consultalo aqui: Aviso de privacidad PicParty';
    const privacyLines = pdf.splitTextToSize(privacyNote, textWidth);
    pdf.textWithLink(privacyLines[0] || privacyNote, margin, y, { url: AVISO_URL });
    if (privacyLines.length > 1) {
      pdf.text(privacyLines.slice(1), margin, y + 3.4);
    }

    // Footer página 1
    addFooter(1, 2);

    // ==================== PÁGINA 2: SERVICIOS Y FIRMAS ====================
    pdf.addPage();

    // === HEADER PÁGINA 2 (mismo estilo) ===
    try {
      pdf.addImage(PICPARTY_LOGO_BASE64, 'PNG', margin, 5, 30, 30);
    } catch(e) {
      console.error("Error cargando logo p2:", e);
      pdf.setFontSize(14);
      pdf.setTextColor(26, 11, 46);
      pdf.setFont(undefined, 'bold');
      pdf.text("PIC PARTY", margin, 20);
    }

    // Servicios debajo del logo
    pdf.setFontSize(7);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(80, 80, 80);
    pdf.text("Cabina de Fotos | Video 360 | Key Moments", margin + 33, 23);

    // QR del aviso de privacidad, entre el logo y el folio
    drawPrivacyQR(128, 3, 20);

    // Folio página 2
    pdf.setFontSize(10);
    pdf.setTextColor(26, 11, 46);
    pdf.setFont(undefined, 'bold');
    pdf.text(folio, pageWidth - margin, 15, { align: 'right' });

    y = 42;

    // === TÍTULO: SERVICIOS CONTRATADOS ===
    pdf.setFillColor(26, 11, 46);
    pdf.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.text("SERVICIOS CONTRATADOS", margin + 5, y + 5.5);
    y += 12;

    // === TABLA DE SERVICIOS ===
    // Cabecera de tabla
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, y, pageWidth - (margin * 2), 7, 'F');
    pdf.setDrawColor(200, 200, 200);
    pdf.rect(margin, y, pageWidth - (margin * 2), 7, 'S');
    pdf.setTextColor(40, 40, 40);
    pdf.setFontSize(9);
    pdf.setFont(undefined, 'bold');
    pdf.text("SERVICIO", margin + 5, y + 5);
    pdf.text("PRECIO", pageWidth - margin - 5, y + 5, { align: 'right' });
    y += 7;

    // Filas de servicios
    pdf.setFont(undefined, 'normal');
    pdf.setDrawColor(220, 220, 220);

    const services = [];
    if (contract.include_cabina && contract.price_cabina > 0) {
      services.push({ name: `Cabina de Fotos - ${contract.cabina_hours || 3} horas`, price: contract.price_cabina });
    }
    if (contract.include_video360 && contract.price_video360 > 0) {
      services.push({ name: `Video 360 - ${contract.video360_hours || 3} horas`, price: contract.price_video360 });
    }
    if (contract.include_key_moments && contract.price_key_moments > 0) {
      services.push({ name: `Key Moments - ${contract.key_moments_pieces || 1} piezas`, price: contract.price_key_moments });
    }
    if (contract.include_live && contract.price_live > 0) {
      services.push({ name: 'PicPartyLive - Galeria Digital', price: contract.price_live });
    }

    services.forEach((svc) => {
      pdf.rect(margin, y, pageWidth - (margin * 2), 7, 'S');
      pdf.text(svc.name, margin + 5, y + 5);
      pdf.text(formatMXN(svc.price), pageWidth - margin - 5, y + 5, { align: 'right' });
      y += 7;
    });

    y += 5;

    // === RESUMEN DE TOTALES ===
    const colRight = pageWidth - margin - 60;
    pdf.setFontSize(9);

    // Subtotal
    pdf.setFont(undefined, 'normal');
    pdf.text("Subtotal:", colRight, y);
    pdf.text(formatMXN(contract.subtotal || (contract.net_price + (contract.discount_amount || 0))), pageWidth - margin, y, { align: 'right' });
    y += 6;

    // Descuento Especial (si aplica)
    if (contract.discount_amount && contract.discount_amount > 0) {
      pdf.setTextColor(200, 50, 50);
      pdf.setFont(undefined, 'bold');
      pdf.text("Descuento Especial:", colRight, y);
      pdf.text(`-${formatMXN(contract.discount_amount)}`, pageWidth - margin, y, { align: 'right' });
      pdf.setTextColor(40, 40, 40);
      pdf.setFont(undefined, 'normal');
      y += 6;
    }

    // TOTAL - Recuadro destacado
    pdf.setFillColor(26, 11, 46);
    pdf.rect(colRight - 5, y, pageWidth - colRight + 5 - margin + 5, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(undefined, 'bold');
    pdf.text("TOTAL:", colRight, y + 5.5);
    pdf.text(formatMXN(contract.net_price), pageWidth - margin, y + 5.5, { align: 'right' });
    y += 12;

    // Anticipo Recibido
    pdf.setTextColor(40, 40, 40);
    pdf.setFont(undefined, 'normal');
    pdf.text("Anticipo Recibido:", colRight, y);
    pdf.text(formatMXN(anticipoMonto), pageWidth - margin, y, { align: 'right' });
    y += 6;

    // SALDO - Destacado
    pdf.setFillColor(200, 180, 100);
    pdf.rect(colRight - 5, y, pageWidth - colRight + 5 - margin + 5, 8, 'F');
    pdf.setTextColor(26, 11, 46);
    pdf.setFont(undefined, 'bold');
    pdf.text("SALDO:", colRight, y + 5.5);
    pdf.text(formatMXN(saldoPendiente > 0 ? saldoPendiente : 0), pageWidth - margin, y + 5.5, { align: 'right' });
    y += 14;

    // === MENSAJE DE SERVICIO LIQUIDADO ===
    if (saldoPendiente <= 0) {
      pdf.setFillColor(34, 139, 34);
      pdf.rect(margin, y, pageWidth - (margin * 2), 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'bold');
      pdf.text("** SERVICIO TOTALMENTE LIQUIDADO **", pageWidth / 2, y + 8, { align: 'center' });
      y += 18;
    }

    // === CORTESÍA / REGALO (si existe) ===
    if (contract.cortesia) {
      pdf.setFillColor(255, 250, 230);
      pdf.rect(margin, y, pageWidth - (margin * 2), 10, 'F');
      pdf.setDrawColor(200, 180, 100);
      pdf.rect(margin, y, pageWidth - (margin * 2), 10, 'S');
      pdf.setTextColor(26, 11, 46);
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'bold');
      pdf.text("CORTESIA INCLUIDA:", margin + 5, y + 6.5);
      pdf.setFont(undefined, 'normal');
      pdf.text(contract.cortesia, margin + 45, y + 6.5);
      y += 16;
    }

    // === ÁREA DE FIRMAS ===
    y = pageHeight - 55;
    pdf.setFillColor(245, 245, 245);
    pdf.rect(margin, y - 5, pageWidth - (margin * 2), 8, 'F');
    pdf.setTextColor(26, 11, 46);
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.text("FIRMAS DE CONFORMIDAD", pageWidth / 2, y, { align: 'center' });
    y += 12;

    pdf.setDrawColor(100, 100, 100);
    pdf.setLineWidth(0.3);

    // Firma EL PRESTADOR (izquierda)
    const firmaY = y + 15;
    pdf.line(margin, firmaY, margin + 70, firmaY);
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(40, 40, 40);
    pdf.text("ADAN OCTAVIO IRIGOYEN", margin + 35, firmaY + 5, { align: 'center' });
    pdf.setFont(undefined, 'bold');
    pdf.text("(EL PRESTADOR)", margin + 35, firmaY + 10, { align: 'center' });

    // Firma EL CLIENTE (derecha)
    pdf.line(pageWidth - margin - 70, firmaY, pageWidth - margin, firmaY);
    pdf.setFont(undefined, 'normal');
    pdf.text("EL CLIENTE", pageWidth - margin - 35, firmaY + 5, { align: 'center' });
    pdf.setFont(undefined, 'bold');
    pdf.text("(FIRMA)", pageWidth - margin - 35, firmaY + 10, { align: 'center' });

    // Footer página 2
    addFooter(2, 2);

    // === GUARDAR PDF (compatible con móviles) ===
    const fileName = `Contrato_${contract.client_name.replace(/\s+/g, '_')}_${folio}.pdf`;
    const pdfBlob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    toast.success(`Contrato ${folio} descargado correctamente`);
  };
